/**
 * The Dictate desktop installers on CrabNebula. These are the `-dictate`
 * public-platform twins of the regular installers
 * (docs/desktop/dictation/DICTATE-BUNDLE.md §4): a pure download, never an
 * update target, that boots into the Dictate first-run onboarding.
 *
 * The marketing site's Dictate page links the same URLs from Sanity; keep the
 * two in step.
 */

/** One installer the download dialog offers. */
export type DictateDesktopInstaller =
  | 'mac-apple-silicon'
  | 'mac-intel'
  | 'windows-exe'
  | 'windows-msi';

/** The CrabNebula release channel: ROW, or the UK-MDR / EU-MDR controlled ones. */
export type DictateInstallerChannel = 'stable' | 'uk' | 'eu';

/**
 * The installer the app resolved for this browser, after mount. Phones,
 * tablets, Linux and anything unrecognised are `unsupported`: no installer is
 * requested for them.
 */
export type DictateDownloadTarget =
  | { os: 'mac'; installer: 'mac-apple-silicon' | 'mac-intel' }
  | { os: 'windows'; installer: 'windows-exe' | 'windows-msi' }
  | { os: 'unsupported'; installer: null };

/**
 * What happened to the download for the current help dialog. `requested` means
 * the request was handed to the browser — never that the file finished saving
 * (the page cannot observe that). `failed` means it could not be handed over.
 */
export type DictateDownloadAttempt = 'not-requested' | 'requested' | 'failed';

/**
 * The name of the hidden iframe the app host keeps mounted for installer
 * downloads. The host points it at the automatic download; the dialog's manual
 * links use it as their `target`, so a retry downloads without leaving the app
 * or opening a popup. Such an anchor IS the download request — nothing else
 * may start one alongside it.
 */
export const DICTATE_INSTALLER_DOWNLOAD_FRAME = 'heidi-dictate-installer-download';

export const DICTATE_DOWNLOAD_INTENT_PARAM = 'dictate_download';
export const DICTATE_DOWNLOAD_INTENT_VALUE = 'dictate-download-pending';
export const DICTATE_DOWNLOAD_INSTALLER_PARAM = 'installer';

/** Final app URL carrying a download intent across regional host redirects. */
export const buildDictateDownloadIntentPath = (
  installer: DictateDesktopInstaller | null = null
): string => {
  const params = new URLSearchParams({
    [DICTATE_DOWNLOAD_INTENT_PARAM]: DICTATE_DOWNLOAD_INTENT_VALUE,
  });
  if (installer) params.set(DICTATE_DOWNLOAD_INSTALLER_PARAM, installer);
  return `/scribe/sessions?${params.toString()}`;
};

const CRABNEBULA_PLATFORM: Record<DictateDesktopInstaller, string> = {
  'mac-apple-silicon': 'dmg-aarch64-dictate',
  'mac-intel': 'dmg-x86_64-dictate',
  'windows-exe': 'nsis-x86_64-dictate',
  'windows-msi': 'wix-x86_64-dictate',
};

/**
 * Whether an untrusted value (a URL query parameter, a stored intent) names one
 * of the four published installers.
 */
export const isDictateDesktopInstaller = (
  value: unknown
): value is DictateDesktopInstaller =>
  typeof value === 'string' && Object.hasOwn(CRABNEBULA_PLATFORM, value);

/**
 * `heidi-scribe/scribe` is production; staging builds publish the same twins to
 * `heidi-scribe/scribe-staging`.
 */
export const buildDictateInstallerUrl = (
  installer: DictateDesktopInstaller,
  {
    channel = 'stable',
    staging = false,
  }: { channel?: DictateInstallerChannel; staging?: boolean } = {}
): string => {
  const app = staging ? 'scribe-staging' : 'scribe';
  const url = `https://cdn.crabnebula.app/download/heidi-scribe/${app}/latest/platform/${CRABNEBULA_PLATFORM[installer]}`;
  return channel === 'stable' ? url : `${url}?channel=${channel}`;
};
